// generic-input.component.ts
import { KeyValue } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  forwardRef,
  input,
  model,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Country } from '@finxone-platform/shared/services';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import { FieldVerificationService } from '../services/field-verification.service';
import { countryCodeToCircleFlag } from '@finxone-platform/shared/frontend';
import { OtpVerificationService } from '@finxone-platform/shared/services';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  InputFormatType,
  InputLabelOptions,
  NavigationOptionsType,
  TypographyKeys,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Subject, map, takeUntil } from 'rxjs';
import { InputValidationFactory } from '../input-field-validation/input-validations-factory';
import { ValidationConfig } from '../input-field-validation/validation-config.type';

@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'finxone-phone-number',
  templateUrl: './finxone-phone-number.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxonePhoneNumberComponent),
      multi: true,
    },
  ],
})
export class FinxonePhoneNumberComponent
  extends InputValidationFactory
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit
{
  private readonly cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);
  private injector = inject(Injector);
  private otpVerificationService = inject(OtpVerificationService);
  private fieldVerification = inject(FieldVerificationService);

  @ViewChild('inputElementWrapper') inputElementWrapper: ElementRef;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Output() verifyPhoneButtonClicked = new EventEmitter<void>();
  @Input() public type = 'text';
  @Input() public placeholder = '';
  @Input() public label = '';
  @Input() public inputMode = 'numeric';
  @Input() public class = '';
  @Input() public readOnly = false;
  @Input() public isDisabled = false;
  @Input() public formControlInvalid = false;
  @Input() public globalCssVariables: Record<string, string> = {};
  @Input() public customInputTemplate?: TemplateRef<unknown>;
  @Input() public customErrorTemplate?: TemplateRef<unknown>;
  @Input() public id = '';
  @Input() public name = '';
  @Input() public value = '';
  @Input() public inputLabelTooltipText = '';
  @Input() public countryCodeFormControl!: FormControl;
  @Input() public helperText: string;

  @Input() public attachToInput: boolean;
  @Input() public showCountryFlag: boolean;
  @Input() public allowSearch: boolean;
  @Input() public showDropdownMenuCountryFlag: boolean;
  @Input() public showCountryName: boolean;
  @Input() public parentControl?: FormControl;

  // Validation Configuration
  @Input() validationConfig: ValidationConfig[] = [];
  @Input() showErrors = true;

  // button inputs
  public isVerifyButtonShown = model<boolean>(false);
  @Input() public isVerifyButtonGlobalStylingAllowed = false;
  @Input() public verifyButtonGlobalStylingClass = '';
  @Input() public verifyButtonStyle: baseCssConfigStyle;
  @Input() public verifyButtonTextContent = '';
  @Input() public countryCode: string;

  @Input() public disableAutocomplete = false;
  @Input() public allowedCountryCodes: string[];

  public parentFormId = input<string>(''); // onboardingFlow
  public nestedFormId = input<string>(''); // onboardingFlow.customForm-sign-up
  public formGroup = input.required<FormGroup>();

  public inputFormat = input<InputFormatType>();
  public navigationOptions = input<NavigationOptionsType>({} as NavigationOptionsType);

  public spaceBetweenElementUnit = input('');
  public spaceBetweenElementValue = input(0);

  protected readonly spaceBetweenItem = computed(() => {
    return `${this.spaceBetweenElementValue()}${this.spaceBetweenElementUnit()}`;
  });

  // Internal form control
  public control = new FormControl();

  public readonly countryCodeControl = new FormControl();

  // Used for ControlValueAccessor
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  private cssVars: Record<string, string> = {};

  public labelType: InputLabelOptions;
  public labelTypeOptions = InputLabelOptions;
  public textStylingClass: TypographyKeys;
  public countries: Country[];
  public countryList: { title: string; currencyFlag: string; key: string; dial_code: string }[];
  private fullCountryList: { title: string; currencyFlag: string; key: string; dial_code: string }[];
  public selectedCountryOption: { title: string; currencyFlag: string; key: string; dial_code: string };
  public isDropdownDisabled = false;
  public borderRadiusClass = '';
  public isInputOnFocus = false;
  public elementContentWidth: number;

  constructor() {
    super();
    effect(() => {
      this.getInputStyles();
    });
  }

  ngOnInit() {
    if (this.parentControl) this.control = this.parentControl;

    this.subscribeToStatusChanges(this.control);
    this.subscribeToStatusChanges(this.countryCodeControl);
    this.onGetCountryList();

    this.fieldVerification.handlePhoneVerification(
      this.injector,
      this.destroy$,
      this.isVerifyButtonShown,
      this.parentFormId(),
      this.formGroup(),
      this.name,
      this.countryCode,
      this.attachToInput,
    );

    this.otpVerificationService.otpVerified$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.field !== 'phone') return;

      this.formGroup().get('isPhoneVerified')?.setValue(true);
      this.fieldVerification.handlePhoneVerification(
        this.injector,
        this.destroy$,
        this.isVerifyButtonShown,
        this.parentFormId(),
        this.formGroup(),
        this.name,
        this.countryCode,
        this.attachToInput,
      );
    });
  }

  public verifyPhone() {
    this.fieldVerification.verifyEmailPhoneButton(
      this.injector,
      this.inputFormat(),
      this.navigationOptions(),
      this.name,
      this.parentFormId(),
      this.nestedFormId(),
      this.attachToInput ?? false,
      this.attachToInput ? undefined : this.countryCodeControl,
      this.formGroup()?.get(this.name),
      this.attachToInput ? undefined : this.countryCode,
    );
  }

  private subscribeToStatusChanges(control: AbstractControl): void {
    control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cd.markForCheck();
    });
  }

  private getInputStyles() {
    const globalCssVariables = this.configService.globalCssVariables();
    if (globalCssVariables) {
      this.cssVars = globalCssVariables;
      // get the label type and text style class from root styles
      this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
      this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
      this.borderRadiusClass = this.getCssVariableValue('--inputFieldBorderRadiusClass');
    }
  }

  private onGetCountryList() {
    this.configService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map((response: Country[]) => {
          this.countries = response;
          this.fullCountryList = [
            {
              title: '',
              currencyFlag: '',
              key: '',
              dial_code: '',
            },
            ...this.countries.map(({ name, code, dial_code }) => ({
              title: name,
              currencyFlag: countryCodeToCircleFlag(code, '24px'),
              key: code,
              dial_code,
            })),
          ];
          this.countryList = this.getAllowedCountriesOptions();
          this.isDropdownDisabled = this.allowedCountryCodes.length === 1;
        }),
      )
      .subscribe();
    this.selectedCountryOption = this.findCountryByKeyAndAssignValue(
      this.countryCodeFormControl.value || '+44',
    );
    this.countryCodeControl.setValue(
      this.countryCodeFormControl.value || this.selectedCountryOption?.dial_code,
    );
  }

  private findCountryByKeyAndAssignValue(countryInput: string): any {
    return this.countryList?.find((country) => country?.dial_code === countryInput);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']?.currentValue !== undefined) {
      this.control.setValue(changes['value']?.currentValue);
    }

    if (changes['validationConfig']?.currentValue !== undefined) {
      this.updateControlValidators();
    }

    if (changes['formControlInvalid']?.currentValue !== undefined) {
      this.setFormControlValidity(this.formControlInvalid);
    }
  }

  ngAfterViewInit(): void {
    this.elementContentWidth = this.inputElementWrapper?.nativeElement.getBoundingClientRect()?.width;
    this.cd.detectChanges();
  }

  private updateControlValidators() {
    const validators = this.setupValidation(this.validationConfig, this.countryCodeControl);
    this.control.setValidators(validators);
    this.control.updateValueAndValidity();
  }

  private setFormControlValidity(formControlInvalid: boolean) {
    if (!formControlInvalid) {
      this.control.markAsPristine();
      this.control.markAsUntouched();
    } else {
      this.control.markAsDirty();
      this.control.markAsTouched();
    }
  }

  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    isDisabled ? this.control.disable() : this.control.enable();
  }

  public onInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.onChange(value);
    this.cd.markForCheck();
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  public getErrorMessageBasedOnKey(error: KeyValue<string, any>): string {
    const key = error.key;
    if (!this.validationConfig.some((config) => config.type === key) && typeof error.value === 'string') {
      return error.value;
    } else {
      const matchingConfig = this.validationConfig.find((config) => config.type === key);
      if (matchingConfig) {
        return this.getErrorMessage(matchingConfig);
      }
    }
    return 'Cannot find error message';
  }

  public handleLabelClick(): void {
    if (this.labelType === this.labelTypeOptions.FLOATING) {
      this.inputElementWrapper?.nativeElement.querySelector('input')?.focus();
    }
  }

  public onCountryChangeEvent() {
    this.selectedCountryOption = this.findCountryByKeyAndAssignValue(this.countryCodeFormControl.value);
    this.countryCodeControl.setValue(this.selectedCountryOption.dial_code);
    this.updateControlValidators();
  }

  private getAllowedCountriesOptions(): {
    title: string;
    currencyFlag: string;
    key: string;
    dial_code: string;
  }[] {
    if (this.allowedCountryCodes.length === 0) {
      return this.fullCountryList;
    }
    return this.fullCountryList.filter((country) => this.allowedCountryCodes.includes(country.title));
  }
}
